import styles from './TopUpBalanceFormModalWindow.module.scss';
import Box from '@components/Box';
import cn from 'classnames';
import FormErrorMessage from '@components/FormErrorMessage';
import TextInputContainer from '@containers/TextInputContainer';
import Title from '@components/Title';
import { Button } from '@components/Button';
import { Column, Grid, Row } from '@components/Grid';
import {
  getPaymentLink,
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  TGetPaymentLinkResponse
} from '@api/apiClient/finance';
import { ITopUpBalanceFormModalWindowProps, TTopUpBalanceForm } from './types';
import { Loader } from '@components/Loader';
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const TopUpBalanceFormModalWindow: React.FC<ITopUpBalanceFormModalWindowProps> = (props: ITopUpBalanceFormModalWindowProps): React.ReactElement => {
  const {
    balanceReplenishmentError,
    balanceReplenishmentInProcessed,
    handlerOnCloseBalanceForm,
    handlerSetLinkTimeout,
    handlerSetQrTimeout,
    handlerWaitingBalanceReplenished,
    replenishmentAmount,
    subTitle
  } = props;

  const [isQrOpen, setQrOpen] = useState<boolean>(false);

  const { account } = useProfile();
  const { t } = useTranslation();

  const {
    data: dataSendTopUpBalance,
    error: errorSendTopUpBalance,
    isError: isErrorSendTopUpBalance,
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      handlerWaitingBalanceReplenished(result.outTradeNo);
      handlerSetQrTimeout();
      setQrOpen(true);
    }
  });

  const {
    error: errorGetPaymentLink,
    isError: isErrorGetPaymentLink,
    isLoading: isLoadingGetPaymentLink,
    mutate: mutateGetPaymentLink
  } = useMutation({
    mutationFn: getPaymentLink,
    onSuccess: (result: TGetPaymentLinkResponse) => {
      handlerWaitingBalanceReplenished(result.paymentRef);
      handlerSetLinkTimeout();
      window.open(result.paymentLink);
    }
  });

  const { control, handleSubmit, setValue, watch } = useForm<TTopUpBalanceForm>({
    defaultValues: {
      amount: replenishmentAmount ? (Math.round(replenishmentAmount * 100) / 100).toString() : ''
    },
    mode: 'onChange'
  });

  const submit = (form: TTopUpBalanceForm): void => {
    if (account?.region === 'CN') {
      mutateSendTopUpBalance({
        amount: +form.amount
      });
    }
    if (account?.region === 'TR') {
      mutateGetPaymentLink(form.amount);
    }
  };

  const amount = watch('amount');

  useEffect(() => {
    setValue('amount', amount.replace(/^0+/, '').replace(/[^0-9.]+/g, ''));
  }, [amount]);

  return (
      <Grid>
        <Row>
          <Column phone={12}>
            <Box pb={subTitle ? 5 : 8}>
              <Title title={t(isQrOpen ? 'TopUpBalanceFormModalWindow.qrTitle' : 'TopUpBalanceFormModalWindow.title')}/>
            </Box>
          </Column>
        </Row>
        {subTitle && (
            <Row>
              <Column phone={12}>
                <Box pb={5}>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.M}
                      type={TextType.SPAN}
                  >
                    {subTitle}
                  </Text>
                </Box>
              </Column>
            </Row>
        )}
        <Row>
          <Column phone={12}>
            <Box pb={8}>
              {isQrOpen ? (
                  <div className={styles.qrCode}>
                    <img
                        alt="qrCode"
                        className={cn(styles.qrCodeImg, balanceReplenishmentInProcessed && styles.qrCodeImgHidden)}
                        src={`data:image/svg+xml;base64,${dataSendTopUpBalance?.qrCode}`}
                    />
                    {balanceReplenishmentInProcessed && <Loader
                        alignment="center"
                        size="xl"
                    />}
                  </div>
              ) : (
                  <TextInputContainer
                      control={control}
                      min={0}
                      name="amount"
                      rules={{
                        required: t('defaultFormErrors.required'),
                        validate: (value) => {
                          if (+value < 1) return t('TopUpBalanceFormModalWindow.lessThanOne', { context: account?.region });
                          return true;
                        },
                      }}
                      title={t('TopUpBalanceFormModalWindow.input')}
                  />
              )}
            </Box>
          </Column>
        </Row>
        <Row
            gap={5}
            noWrap
        >
          {isQrOpen ? (
              <Box>
                <Button
                    className={styles.action}
                    disabled={balanceReplenishmentInProcessed}
                    onClick={handlerOnCloseBalanceForm}
                    title={t('TopUpBalanceFormModalWindow.cancel')}
                />
              </Box>
          ) : (
              <Box>
                <Button
                    className={styles.action}
                    onClick={handleSubmit(submit)}
                    pending={isLoadingSendTopUpBalance || isLoadingGetPaymentLink}
                    title={t('TopUpBalanceFormModalWindow.buttonSend')}
                />
              </Box>
          )}
        </Row>
        {isErrorSendTopUpBalance && <FormErrorMessage error={errorSendTopUpBalance}/>}
        {isErrorGetPaymentLink && <FormErrorMessage error={errorGetPaymentLink}/>}
        {balanceReplenishmentError && <FormErrorMessage error={balanceReplenishmentError}/>}
      </Grid>
  );
};

export default TopUpBalanceFormModalWindow;
